import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppTabPanel } from '../shared/tabs/AppTabPanel';
import { AppTabs } from '../shared/tabs/AppTabs';
import { FilterSelect } from '../shared/table/FilterSelect';
import {
    getTicketTypeLabel,
    TICKET_IMPACT_OPTIONS,
    TICKET_PRIORITY_OPTIONS,
    TICKET_SOURCE_OPTIONS,
    TICKET_TYPE_OPTIONS,
    TICKET_URGENCY_OPTIONS,
} from '../../../../helpers/ticket-label-helper';
import { ADDITIONAL_FIELD_TYPES, useTaskCreateForm } from './hooks';
import { getFollowAuthorAvatar } from './helpers/taskCreateUtils';

export const TaskCreateScreen = ({ asModal = false, onClose, onCreated }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token, id: authUserId } = useSelector((state) => state.auth);

    const {
        activeTab,
        setActiveTab,
        isSaving,
        isLoadingLookups,
        isLoadingAdditionalFields,
        error,
        customers,
        categories,
        subcategories,
        additionalFieldSections,
        additionalFieldValues,
        tagInput,
        form,
        availableProjects,
        selectedCustomerLabel,
        selectedProjectLabel,
        tagBadges,
        users,
        groups,
        handleAddTag,
        handleRemoveTag,
        handleInputChange,
        handleFormSelectChange,
        handleAttachmentsChange,
        handleAdditionalFieldChange,
        handleAdditionalFieldSelectChange,
        handleDescriptionHtmlChange,
        handleSubmit,
        handleCancel,
        setTagInput,
    } = useTaskCreateForm({
        token,
        authUserId,
        dispatch,
        navigate,
        asModal,
        onClose,
        onCreated,
    });

    const attachmentsInputRef = React.useRef(null);

    const customerOptions = React.useMemo(() => ([
        { value: '', label: 'Selecciona un cliente' },
        ...customers.map((customer) => ({
            value: customer._id,
            label: `${customer.code} - ${customer.name}`,
        })),
    ]), [customers]);

    const projectOptions = React.useMemo(() => ([
        { value: '', label: form.customer ? 'Selecciona un proyecto' : 'Primero selecciona un cliente' },
        ...availableProjects.map((project) => ({
            value: project._id,
            label: `${project.code} - ${project.name}`,
        })),
    ]), [availableProjects, form.customer]);

    const baseTabs = React.useMemo(() => ([
        { key: 'general', label: 'General' },
        { key: 'classification', label: 'Clasificación' },
    ]), []);

    const additionalGroupTabs = React.useMemo(() => {
        return additionalFieldSections.map((section) => ({
            key: `additional-${section.key}`,
            label: section.label,
        }));
    }, [additionalFieldSections]);

    const shouldShowAdditionalTabs = additionalGroupTabs.length > 0;
    const isLoadingAdditionalTabs = Boolean(form.project) && isLoadingAdditionalFields;

    const createTabs = React.useMemo(() => {
        if (!shouldShowAdditionalTabs) {
            return baseTabs;
        }

        return [...baseTabs, ...additionalGroupTabs];
    }, [baseTabs, additionalGroupTabs, shouldShowAdditionalTabs]);

    React.useEffect(() => {
        const validKeys = new Set(createTabs.map((tab) => tab.key));
        if (!validKeys.has(activeTab)) {
            setActiveTab('general');
        }
    }, [activeTab, createTabs, setActiveTab]);

    return (
        <div className={asModal ? '' : 'row'}>
            <div className={asModal ? 'col-12' : 'col-12 col-xl-9 col-xxl-8 mx-auto'}>
                <div className="card card__no-border ticket-create-screen">
                    <div className="card-body">
                        {!asModal && (
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h5 className="fw-bold mb-0">Nuevo ticket</h5>
                                <button type="button" className="btn btn-light-brand" onClick={handleCancel}>
                                    Volver
                                </button>
                            </div>
                        )}

                        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}

                        <div className="border rounded p-3 mb-3 bg-light">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                <div>
                                    <h6 className="fw-semibold mb-0">Contexto del ticket</h6>
                                    <small className="text-muted">Selecciona primero cliente y proyecto para trabajar con el contexto correcto.</small>
                                </div>
                                <div className="d-flex flex-wrap gap-1">
                                    <span className="badge bg-soft-primary text-primary">Cliente: {selectedCustomerLabel}</span>
                                    <span className="badge bg-soft-info text-info">Proyecto: {selectedProjectLabel}</span>
                                    <span className="badge bg-soft-warning text-warning">Tipo: {getTicketTypeLabel(form.type)}</span>
                                </div>
                            </div>

                            <div className="row g-2">
                                <div className="col-md-6">
                                    <label className="form-label">Cliente</label>
                                    <FilterSelect
                                        value={form.customer}
                                        options={customerOptions}
                                        searchable
                                        searchPlaceholder="Buscar cliente..."
                                        onChange={(nextValue) => handleFormSelectChange('customer', nextValue)}
                                        disabled={isLoadingLookups}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Proyecto</label>
                                    <FilterSelect
                                        value={form.project}
                                        options={projectOptions}
                                        searchable
                                        searchPlaceholder="Buscar proyecto..."
                                        onChange={(nextValue) => handleFormSelectChange('project', nextValue)}
                                        disabled={isLoadingLookups || !form.customer}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="ticket-detail-main-tabs mb-3">
                            <AppTabs
                                tabs={createTabs}
                                activeTab={activeTab}
                                onChange={setActiveTab}
                                navClassName="nav flex-row flex-wrap ticket-detail-top-tabs"
                                itemClassName="nav-item"
                            />
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="tab-content border rounded p-3">
                                <AppTabPanel isActive={activeTab === 'general'} className="p-0">
                                    <div className="row g-2 g-md-3">
                                        <div className='col-12 col-md-8'>
                                            <div className="col-12">
                                                <label className="form-label">Título</label>
                                                <input className="form-control" name="title" value={form.title} onChange={handleInputChange} required />
                                            </div>

                                            <div className="col-12 mt-3">
                                                <label className="form-label">Descripción</label>
                                                <div
                                                    className="form-control"
                                                    contentEditable
                                                    suppressContentEditableWarning
                                                    onInput={handleDescriptionHtmlChange}
                                                    style={{ minHeight: '155px', overflowY: 'auto' }}
                                                />
                                            </div>

                                            <div className="col-12 mt-2 d-flex align-items-center gap-2">
                                                <div className="ticket-file-input">
                                                    <input
                                                        ref={attachmentsInputRef}
                                                        type="file"
                                                        className="ticket-file-input__native"
                                                        multiple
                                                        onChange={handleAttachmentsChange}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="ticket-file-input__trigger"
                                                        onClick={() => attachmentsInputRef.current?.click()}
                                                    >
                                                        <i className="bi bi-paperclip" aria-hidden="true" />
                                                        Adjuntos
                                                    </button>
                                                </div>
                                                {form.attachments.length > 0 && (
                                                    <div className="text-muted fs-12">{form.attachments.map((file) => file.name).join(' · ')}</div>
                                                )}
                                            </div>

                                            <div className="col-12 mt-3">
                                                <label className="form-label">Etiquetas</label>
                                                <div className="d-flex align-items-center gap-2 mt-2">
                                                    <input
                                                        className="form-control"
                                                        value={tagInput}
                                                        onChange={(event) => setTagInput(event.target.value || '')}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter' || event.key === ',') {
                                                                event.preventDefault();
                                                                handleAddTag();
                                                            }
                                                        }}
                                                        placeholder="Escribe una etiqueta y presiona Enter"
                                                    />
                                                    <button type="button" className="btn btn-light-brand" onClick={handleAddTag}>
                                                        Agregar
                                                    </button>
                                                </div>
                                                {tagBadges.length > 0 && (
                                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                                        {tagBadges.map((tag) => (
                                                            <span key={`create-tag-${tag}`} className="badge bg-soft-primary text-primary d-inline-flex align-items-center gap-1">
                                                                <span>{tag}</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 text-primary lh-1"
                                                                    onClick={() => handleRemoveTag(tag)}
                                                                    aria-label={`Quitar etiqueta ${tag}`}
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className='col-12 col-md-4'>
                                             <div className="col-12">
                                                <label className="form-label">Tipo</label>
                                                <FilterSelect
                                                    value={form.type}
                                                    options={TICKET_TYPE_OPTIONS}
                                                    onChange={(nextValue) => handleFormSelectChange('type', nextValue)}
                                                />
                                            </div>

                                            <div className="col-12 mt-3">
                                                <label className="form-label">Fuente</label>
                                                <FilterSelect
                                                    value={form.source}
                                                    options={TICKET_SOURCE_OPTIONS}
                                                    onChange={(nextValue) => handleFormSelectChange('source', nextValue)}
                                                />
                                            </div>

                                            <div className="col-12 mt-3">
                                                <label className="form-label">Solicitante</label>
                                                <FilterSelect
                                                    value={form.requester}
                                                    searchable
                                                    searchPlaceholder="Buscar solicitante..."
                                                    options={[
                                                        { value: '', label: 'Sin solicitante' },
                                                        ...users.map((user) => ({
                                                            value: user._id,
                                                            label: user.name || user.email,
                                                            meta: user.email || '',
                                                            avatar: getFollowAuthorAvatar(user),
                                                        })),
                                                    ]}
                                                    onChange={(nextValue) => handleFormSelectChange('requester', nextValue)}
                                                />
                                            </div>

                                            <div className="col-12 mt-3">
                                                <label className="form-label">Asignado a</label>
                                                <FilterSelect
                                                    value={form.assignee}
                                                    searchable
                                                    searchPlaceholder="Buscar asignado..."
                                                    options={[
                                                        { value: '', label: 'Sin asignar' },
                                                        ...users.map((user) => ({
                                                            value: user._id,
                                                            label: user.name || user.email,
                                                            meta: user.email || '',
                                                            avatar: getFollowAuthorAvatar(user),
                                                        })),
                                                    ]}
                                                    onChange={(nextValue) => handleFormSelectChange('assignee', nextValue)}
                                                />
                                            </div>

                                            <div className="col-12 mt-3">
                                                <label className="form-label">Grupo</label>
                                                <FilterSelect
                                                    value={form.group}
                                                    searchable
                                                    searchPlaceholder="Buscar grupo..."
                                                    options={[
                                                        { value: '', label: 'Sin grupo', icon: 'bi bi-people' },
                                                        ...groups.map((group) => ({
                                                            value: group.name,
                                                            label: `${group.code} - ${group.name}`,
                                                            meta: group.description || '',
                                                            icon: 'bi bi-people',
                                                        })),
                                                    ]}
                                                    onChange={(nextValue) => handleFormSelectChange('group', nextValue)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </AppTabPanel>

                                <AppTabPanel isActive={activeTab === 'classification'} className="p-0">
                                    <div className="row g-2">
                                        <div className='col-md-6'>
                                            <div className="col-md-6">
                                                <label className="form-label">Prioridad</label>
                                                <FilterSelect
                                                    value={form.priority}
                                                    options={TICKET_PRIORITY_OPTIONS}
                                                    onChange={(nextValue) => handleFormSelectChange('priority', nextValue)}
                                                />
                                            </div>

                                            <div className="col-md-6 mt-3">
                                                <label className="form-label">Impacto</label>
                                                <FilterSelect
                                                    value={form.impact}
                                                    options={TICKET_IMPACT_OPTIONS}
                                                    onChange={(nextValue) => handleFormSelectChange('impact', nextValue)}
                                                />
                                            </div>

                                            <div className="col-md-6 mt-3">
                                                <label className="form-label">Urgencia</label>
                                                <FilterSelect
                                                    value={form.urgency}
                                                    options={TICKET_URGENCY_OPTIONS}
                                                    onChange={(nextValue) => handleFormSelectChange('urgency', nextValue)}
                                                />
                                            </div>
                                        </div>
                                        <div className='col'>
                                            <div className="col-md-6">
                                                <label className="form-label">Categoría</label>
                                                <FilterSelect
                                                    value={form.category}
                                                    options={[
                                                        { value: '', label: form.project ? 'Sin categoría' : 'Selecciona primero un proyecto' },
                                                        ...categories.map((category) => ({ value: category._id, label: category.name })),
                                                    ]}
                                                    onChange={(nextValue) => handleFormSelectChange('category', nextValue)}
                                                    disabled={!form.project}
                                                />
                                            </div>

                                            <div className="col-md-6 mt-3">
                                                <label className="form-label">Subcategoría</label>
                                                <FilterSelect
                                                    value={form.subcategory}
                                                    options={[
                                                        { value: '', label: form.project ? 'Sin subcategoría' : 'Selecciona primero un proyecto' },
                                                        ...subcategories.map((subcategory) => ({ value: subcategory._id, label: subcategory.name })),
                                                    ]}
                                                    onChange={(nextValue) => handleFormSelectChange('subcategory', nextValue)}
                                                    disabled={!form.project}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </AppTabPanel>

                                {additionalGroupTabs.map((tab) => {
                                    const section = additionalFieldSections.find((item) => `additional-${item.key}` === tab.key);
                                    if (!section) {
                                        return null;
                                    }

                                    return (
                                        <AppTabPanel key={`panel-${tab.key}`} isActive={activeTab === tab.key} className="p-0">
                                            <div className="mb-3">
                                                <div className="row g-2">
                                                    {section.fields.map((field) => {
                                                        const fieldKey = field.key || field._id;
                                                        if (!fieldKey) {
                                                            return null;
                                                        }

                                                        const fieldType = field.type;
                                                        const fieldLabel = `${field.name || fieldKey}${field.required ? ' *' : ''}`;

                                                        if (fieldType === ADDITIONAL_FIELD_TYPES.CHECKBOX) {
                                                            return (
                                                                <div className="col-12" key={field._id || fieldKey}>
                                                                    <div className="form-check mt-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            id={`custom-field-${fieldKey}`}
                                                                            checked={Boolean(additionalFieldValues[fieldKey])}
                                                                            onChange={(event) => handleAdditionalFieldChange(event, fieldKey)}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`custom-field-${fieldKey}`}>
                                                                            &nbsp;{fieldLabel}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        if (fieldType === ADDITIONAL_FIELD_TYPES.LONG_TEXT) {
                                                            return (
                                                                <div className="col-12" key={field._id || fieldKey}>
                                                                    <label className="form-label">{fieldLabel}</label>
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows={4}
                                                                        value={additionalFieldValues[fieldKey] || ''}
                                                                        onChange={(event) => handleAdditionalFieldChange(event, fieldKey)}
                                                                        required={Boolean(field.required)}
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        if (fieldType === ADDITIONAL_FIELD_TYPES.LIST) {
                                                            const options = Array.isArray(field.options) ? field.options : [];
                                                            return (
                                                                <div className="col-md-6" key={field._id || fieldKey}>
                                                                    <label className="form-label">{fieldLabel}</label>
                                                                    <FilterSelect
                                                                        value={additionalFieldValues[fieldKey] || ''}
                                                                        options={[
                                                                            { value: '', label: 'Selecciona una opción' },
                                                                            ...options.map((option) => ({ value: option, label: option })),
                                                                        ]}
                                                                        onChange={(nextValue) => handleAdditionalFieldSelectChange(fieldKey, nextValue)}
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <div className="col-md-6" key={field._id || fieldKey}>
                                                                <label className="form-label">{fieldLabel}</label>
                                                                <input
                                                                    className="form-control"
                                                                    type={fieldType === ADDITIONAL_FIELD_TYPES.NUMBER ? 'number' : 'text'}
                                                                    value={additionalFieldValues[fieldKey] || ''}
                                                                    onChange={(event) => handleAdditionalFieldChange(event, fieldKey)}
                                                                    required={Boolean(field.required)}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </AppTabPanel>
                                    );
                                })}

                                {isLoadingAdditionalTabs && (
                                    <div className="border rounded p-3 bg-light ticket-detail-enter mt-3">
                                        <div className="d-flex align-items-center gap-2 text-primary fw-semibold">
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            Cargando campos adicionales y categorias...
                                        </div>
                                    </div>
                                )}

                                {!isLoadingAdditionalTabs && form.project && !shouldShowAdditionalTabs && (
                                    <div className="text-muted rounded p-2 bg-light ticket-detail-enter mt-3">
                                        Este proyecto no tiene grupos de campos adicionales.
                                    </div>
                                )}
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-3">
                                {
                                    asModal && (
                                    <button type="button" className="btn btn-light-brand" onClick={handleCancel} disabled={isSaving}>
                                        Cancelar
                                    </button>
                                    )
                                        
                                }
                                <button type="submit" className="btn btn-primary" disabled={isSaving || isLoadingLookups}>
                                    {isSaving ? 'Guardando...' : 'Crear ticket'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
